import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import { useGlobal } from '../Context/GlobalContext';
import { useWorkspace } from './WorkspaceContext';
import { useToast } from './ToastContext';
import { useLanguage } from '../Context/LanguageContext';

const NotificationContext = createContext();

// Adaptive polling intervals
const POLLING_INTERVALS = {
  ACTIVE: 2000,    // 2s when dropdown open or recent activity
  NORMAL: 5000,    // 5s default (reduced from 1s to prevent spam)
  IDLE: 30000      // 30s when tab hidden or long idle
};

export const NotificationProvider = ({ children }) => {
  const { apiUrl } = useGlobal();
  const { currentWorkspace } = useWorkspace();
  const { addToast } = useToast();
  const { t } = useLanguage();
  const location = useLocation();

  // State
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [hasMore, setHasMore] = useState(false);
  const [cursor, setCursor] = useState(null);

  // Active executions state
  const [activeExecutions, setActiveExecutions] = useState([]);
  const [activeExecutionCount, setActiveExecutionCount] = useState(0);
  const [isExecutionsDropdownOpen, setIsExecutionsDropdownOpen] = useState(false);

  // Adaptive polling state
  const [pollingInterval, setPollingInterval] = useState(POLLING_INTERVALS.NORMAL);
  const failureCountRef = useRef(0);
  const pollIntervalRef = useRef(null);
  const lastFetchRef = useRef(0);
  const previousUnreadCountRef = useRef(-1); // -1 means initial state, won't trigger toast on first fetch
  const locationPathnameRef = useRef(location.pathname); // Store location pathname in ref to avoid recreating callbacks

  // Circuit breaker
  const MAX_FAILURES = 3;
  const CIRCUIT_RESET_TIME = 30000; // 30 seconds

  // Get auth token
  const getAuthToken = useCallback(() => {
    return localStorage.getItem('brandvox_token');
  }, []);

  // Get translated toast content based on notification type
  const getToastContent = useCallback((notification) => {
    const { type, metadata } = notification;

    if (type === 'image_generation_complete') {
      const name = metadata?.name || 'Untitled';
      const variationCount = metadata?.variation_count || 1;
      const title = t('notifications.imageGenerationComplete.title');
      const message = variationCount > 1
        ? t('notifications.imageGenerationComplete.messagePlural')
          .replace('{count}', variationCount)
          .replace('{name}', name)
        : t('notifications.imageGenerationComplete.message').replace('{name}', name);
      return { title, message };
    }

    if (type === 'social_media_complete') {
      const name = metadata?.name || 'Untitled';
      const platform = metadata?.platform || 'social media';
      const variationCount = metadata?.variation_count || 1;
      const title = t('notifications.socialMediaComplete.title');
      const message = variationCount > 1
        ? t('notifications.socialMediaComplete.messagePlural')
          .replace('{count}', variationCount)
          .replace('{platform}', platform)
          .replace('{name}', name)
        : t('notifications.socialMediaComplete.message')
          .replace('{platform}', platform)
          .replace('{name}', name);
      return { title, message };
    }

    if (type === 'video_generation_complete') {
      const name = metadata?.name || 'Untitled';
      const variationCount = metadata?.variation_count || 1;
      const title = t('notifications.videoGenerationComplete.title');
      const message = variationCount > 1
        ? t('notifications.videoGenerationComplete.messagePlural')
          .replace('{count}', variationCount)
          .replace('{name}', name)
        : t('notifications.videoGenerationComplete.message').replace('{name}', name);
      return { title, message };
    }

    if (type === 'seo_analysis_complete') {
      const name = metadata?.name || 'Unknown Domain';
      const variationCount = metadata?.variation_count || 1;
      const title = t('notifications.seoAnalysisComplete.title');
      const message = variationCount > 1
        ? t('notifications.seoAnalysisComplete.messagePlural')
          .replace('{count}', variationCount)
          .replace('{name}', name)
        : t('notifications.seoAnalysisComplete.message').replace('{name}', name);
      return { title, message };
    }

    return {
      title: notification.title,
      message: notification.message,
    };
  }, [t]);

  // Fetch full notifications list
  const fetchNotifications = useCallback(async (unreadOnly = false, loadMore = false) => {
    const token = getAuthToken();
    if (!token || !currentWorkspace?.workspace_id) return;

    setLoading(true);
    try {
      const params = new URLSearchParams({
        workspace_id: currentWorkspace.workspace_id,
        unread_only: unreadOnly,
      });

      if (loadMore && cursor) {
        params.append('cursor', cursor);
      }

      const response = await fetch(
        `${apiUrl}/api/v1/notifications?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();

        if (loadMore) {
          setNotifications(prev => [...prev, ...data.notifications]);
        } else {
          setNotifications(data.notifications);
        }

        setUnreadCount(data.unread_count);
        setHasMore(data.has_more);
        setCursor(data.cursor || null);
        failureCountRef.current = 0;

        // Update active executions from the response
        if (data.active_executions) {
          setActiveExecutions(data.active_executions);
          setActiveExecutionCount(data.active_executions.length);
        }
      }
    } catch (error) {
    } finally {
      setLoading(false);
    }
  }, [apiUrl, currentWorkspace, cursor, getAuthToken]);

  // Mark single notification as read (optimistic update)
  const markAsRead = useCallback(async (notificationId) => {
    const token = getAuthToken();
    if (!token || !currentWorkspace?.workspace_id) return;

    // Optimistic update
    setNotifications(prev =>
      prev.map(n => n.notification_id === notificationId ? { ...n, is_read: true } : n)
    );
    setUnreadCount(prev => Math.max(0, prev - 1));

    try {
      await fetch(
        `${apiUrl}/api/v1/notifications/${notificationId}/read?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
    } catch (error) {
      // Revert on failure
      fetchNotifications();
    }
  }, [apiUrl, currentWorkspace, fetchNotifications, getAuthToken]);

  // Fetch the latest notification and show a toast (if user is not on the origin page)
  const fetchLatestAndShowToast = useCallback(async () => {
    const token = getAuthToken();
    if (!token || !currentWorkspace?.workspace_id) return;

    try {
      const params = new URLSearchParams({
        workspace_id: currentWorkspace.workspace_id,
        limit: '1',
        unread_only: 'true',
      });

      const response = await fetch(
        `${apiUrl}/api/v1/notifications?${params.toString()}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.notifications && data.notifications.length > 0) {
          const latestNotification = data.notifications[0];

          // Check if user is on a different page than where generation was started
          // Navigation paths like /dashboard/image-creation, /dashboard/video-creation etc.
          const notificationPath = latestNotification.metadata?.navigation_path || '';
          const currentPath = locationPathnameRef.current; // Use ref instead of location.pathname

          // Determine the "origin" path based on notification type
          const originPathMap = {
            'image_generation_complete': '/image-generation-creator',
            'video_generation_complete': '/video-generation-creator',
            'social_media_complete': '/social-media-creator',
            'seo_analysis_complete': '/seo-analysis-creator',
          };

          const originPath = originPathMap[latestNotification.type] || '';

          // Only show toast if user is NOT on the origin page (where they started the generation)
          if (originPath && currentPath !== originPath) {
            const { title, message } = getToastContent(latestNotification);

            addToast({
              type: latestNotification.type,
              title,
              message,
              metadata: {
                notification_id: latestNotification.notification_id,
                navigation_path: notificationPath,
                image_url: latestNotification.metadata?.image_url,
              },
            });
          } else if (originPath && currentPath === originPath) {
            // User stayed on the page - mark notification as read (no toast)
            markAsRead(latestNotification.notification_id);
          }
        }
      }
    } catch (error) {
    }
  }, [apiUrl, currentWorkspace, getAuthToken, getToastContent, addToast, markAsRead]); // Removed location.pathname dependency

  // Fetch unread count (lightweight, for polling)
  const fetchUnreadCount = useCallback(async () => {
    const token = getAuthToken();
    if (!token || !currentWorkspace?.workspace_id) return;

    // Circuit breaker check
    if (failureCountRef.current >= MAX_FAILURES) {
      const now = Date.now();
      if (now - lastFetchRef.current < CIRCUIT_RESET_TIME) {
        return;
      }
      // Reset circuit breaker after timeout
      failureCountRef.current = 0;
    }

    try {
      const response = await fetch(
        `${apiUrl}/api/v1/notifications/count?workspace_id=${currentWorkspace.workspace_id}`,
        {
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        const newCount = data.count;

        // Check if unread count increased - a new notification arrived
        if (newCount > previousUnreadCountRef.current && previousUnreadCountRef.current >= 0) {
          // Fetch and show toast for the new notification (debounced to prevent spam)
          // Only fetch if enough time has passed since last fetch
          const timeSinceLastFetch = Date.now() - lastFetchRef.current;
          if (timeSinceLastFetch > 2000) { // At least 2 seconds between fetches
            fetchLatestAndShowToast();
          }
        }

        // Update refs and state
        previousUnreadCountRef.current = newCount;
        setUnreadCount(newCount);
        failureCountRef.current = 0;

        // Update active execution count from the response
        if (data.active_execution_count !== undefined) {
          setActiveExecutionCount(data.active_execution_count);
        }
      } else {
        throw new Error('Failed to fetch count');
      }
    } catch (error) {
      failureCountRef.current++;
      if (failureCountRef.current >= MAX_FAILURES) {
        setPollingInterval(POLLING_INTERVALS.IDLE);
      }
    }

    lastFetchRef.current = Date.now();
  }, [apiUrl, currentWorkspace, getAuthToken, fetchLatestAndShowToast]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    const token = getAuthToken();
    if (!token || !currentWorkspace?.workspace_id) return;

    // Optimistic update
    setNotifications(prev => prev.map(n => ({ ...n, is_read: true })));
    setUnreadCount(0);

    try {
      await fetch(
        `${apiUrl}/api/v1/notifications/read-all?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
    } catch (error) {
      fetchNotifications();
    }
  }, [apiUrl, currentWorkspace, fetchNotifications, getAuthToken]);

  // Delete a notification
  const deleteNotification = useCallback(async (notificationId) => {
    const token = getAuthToken();
    if (!token || !currentWorkspace?.workspace_id) return;

    // Optimistic update
    const wasUnread = notifications.find(n => n.notification_id === notificationId)?.is_read === false;
    setNotifications(prev => prev.filter(n => n.notification_id !== notificationId));
    if (wasUnread) {
      setUnreadCount(prev => Math.max(0, prev - 1));
    }

    try {
      await fetch(
        `${apiUrl}/api/v1/notifications/${notificationId}?workspace_id=${currentWorkspace.workspace_id}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': `Bearer ${token}`,
          },
        }
      );
    } catch (error) {
      fetchNotifications();
    }
  }, [apiUrl, currentWorkspace, notifications, fetchNotifications, getAuthToken]);

  // Update location pathname ref when it changes
  useEffect(() => {
    locationPathnameRef.current = location.pathname;
  }, [location.pathname]);

  // Handle visibility change for adaptive polling
  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.hidden) {
        setPollingInterval(POLLING_INTERVALS.IDLE);
      } else {
        setPollingInterval(isDropdownOpen ? POLLING_INTERVALS.ACTIVE : POLLING_INTERVALS.NORMAL);
        // Fetch immediately when becoming visible
        fetchUnreadCount();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [fetchUnreadCount, isDropdownOpen]);

  // Set up polling interval
  useEffect(() => {
    const token = getAuthToken();
    if (!token || !currentWorkspace?.workspace_id) {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
      return;
    }

    // Initial fetch
    fetchUnreadCount();

    // Set up polling
    pollIntervalRef.current = setInterval(fetchUnreadCount, pollingInterval);

    return () => {
      if (pollIntervalRef.current) {
        clearInterval(pollIntervalRef.current);
        pollIntervalRef.current = null;
      }
    };
  }, [currentWorkspace, pollingInterval, fetchUnreadCount, getAuthToken]);

  // Dropdown open = faster polling and fetch full list
  useEffect(() => {
    if (isDropdownOpen || isExecutionsDropdownOpen) {
      setPollingInterval(POLLING_INTERVALS.ACTIVE);
      fetchNotifications();
    } else {
      setPollingInterval(POLLING_INTERVALS.NORMAL);
    }
  }, [isDropdownOpen, isExecutionsDropdownOpen, fetchNotifications]);

  // Reset state when workspace changes
  useEffect(() => {
    setNotifications([]);
    setUnreadCount(0);
    setCursor(null);
    setHasMore(false);
    failureCountRef.current = 0;
    previousUnreadCountRef.current = -1; // Reset to -1 to prevent toast on first fetch
    setPollingInterval(POLLING_INTERVALS.NORMAL);
    // Reset active executions state
    setActiveExecutions([]);
    setActiveExecutionCount(0);
  }, [currentWorkspace?.workspace_id]);

  const value = {
    notifications,
    unreadCount,
    loading,
    isDropdownOpen,
    setIsDropdownOpen,
    hasMore,
    fetchNotifications,
    fetchUnreadCount,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    loadMore: () => fetchNotifications(false, true),
    // Active executions
    activeExecutions,
    activeExecutionCount,
    isExecutionsDropdownOpen,
    setIsExecutionsDropdownOpen,
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (!context) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

export default NotificationContext;
